// ============================================================
// validation.ts
// Form validation utilities for Casevo
// Used by all section components to validate required fields
// ============================================================
import { Case, ClientSource } from '../types/case.types'
import { isValidNiNumber } from './niNumber'
import { emailFormatError, phoneFormatError } from './contactFormat'
export interface ValidationError {
  field: string
  message: string
}
// Client Source validation. N/A short-circuits to no errors —
// when ticked, the section is completable with empty fields.
// When not N/A, require: name, contact number, email, postcode.
export const validateClientSource = (cs: ClientSource): ValidationError[] => {
  if (cs.notApplicable) return []
  const errors: ValidationError[] = []
  if (!cs.name.trim()) errors.push({ field: 'clientSourceName', message: 'Client source name is required' })
  if (!cs.contactNumber.trim()) errors.push({ field: 'clientSourceContactNumber', message: 'Contact number is required' })
  if (!cs.emailAddress.trim()) errors.push({ field: 'clientSourceEmailAddress', message: 'Email address is required' })
  if (!cs.addressPostcode.trim()) errors.push({ field: 'clientSourceAddressPostcode', message: 'Postcode is required' })
  return errors
}
// Stage 2b: validateClientDetails reads the person-level fields from
// the case-row CLIENT SNAPSHOT (currentCase.clientDetails) — the
// editable copy the case-page section now writes. The earlier Phase-4
// shape took a separate `client: Client | null` arg to read live data;
// after the untangle the snapshot IS the editable source-of-truth for
// the case page, so validation follows the editor. The 4 case-specific
// fields (timeOffWork, daysOffWork, descriptionOfInjuries,
// medicalAttention) deliberately don't gate "Mark complete" — they're
// informational and may legitimately be empty.
// Person-shaped validation shared by the case page (strict) and the client
// page's Client Details (lenient). The client-info sections component
// (components/clientInfo) calls this; validateClientDetails below is now a
// thin strict wrapper — same rules, same messages, same order as before.
//   strict  — case rules: forename, surname, DOB, NI (when Yes), phone and
//             email are all required.
//   lenient — client record: forename + surname required (a client must
//             have a name); NI number format-checked only once one is typed.
export interface PersonForValidation {
  forename: string
  surname: string
  dateOfBirth: string | null
  hasNiNumber: boolean | null
  niNumber: string
  mainContactNumber: string
  emailAddress: string
  // B3 — false = "Client has no email address": the email isn't required.
  hasEmailAddress?: boolean | null
}
const NI_FORMAT_MESSAGE = 'NI number must be 9 characters: 2 letters, 6 digits, then 1 letter'
// THE shared NI rule (utils/niNumber — normalise, then match; the AI
// briefing's redaction on the server uses the same rule).
const niValid = isValidNiNumber
export const validatePerson = (d: PersonForValidation, opts: { strict: boolean }): ValidationError[] => {
  const errors: ValidationError[] = []
  if (!(d.forename ?? '').trim()) errors.push({ field: 'forename', message: 'Forename is required' })
  if (!(d.surname ?? '').trim()) errors.push({ field: 'surname', message: 'Surname is required' })
  if (opts.strict && !d.dateOfBirth) errors.push({ field: 'dateOfBirth', message: 'Date of birth is required' })
  // Only validate the NI number when the client has explicitly said
  // they have one. null = unanswered (don't nag yet), false = they
  // don't have one (an empty value is the correct answer).
  //
  // Order matters: surface "required" first when empty, then "format"
  // when the user has entered something but it doesn't parse. Both
  // errors live on the same field, and getError() picks the first
  // match, so the else-if keeps them mutually exclusive.
  //
  // The regex tolerates whitespace between groups by stripping all
  // spaces before testing — "AB 12 34 56 C" and "AB123456C" both pass.
  // Case-insensitive flag means "ab123456c" is also accepted. We do
  // NOT auto-format the stored value; the user types whatever they
  // like, we just check it's parseable as a UK NI number.
  if (d.hasNiNumber === true) {
    const ni = d.niNumber ?? ''
    if (!ni.trim()) {
      if (opts.strict) errors.push({ field: 'niNumber', message: 'NI number is required' })
    } else if (!niValid(ni)) {
      errors.push({ field: 'niNumber', message: NI_FORMAT_MESSAGE })
    }
  }
  if (opts.strict && !(d.mainContactNumber ?? '').trim()) errors.push({ field: 'mainContactNumber', message: 'Main contact number is required' })
  // A typed phone / email must be a real one (utils/contactFormat) — on the
  // case's copy AND the client record (both use this).
  else {
    const phoneBad = phoneFormatError(d.mainContactNumber)
    if (phoneBad) errors.push({ field: 'mainContactNumber', message: phoneBad })
  }
  if (opts.strict && d.hasEmailAddress !== false && !(d.emailAddress ?? '').trim()) errors.push({ field: 'emailAddress', message: 'Email address is required' })
  else if (d.hasEmailAddress !== false) {
    const emailBad = emailFormatError(d.emailAddress)
    if (emailBad) errors.push({ field: 'emailAddress', message: emailBad })
  }
  return errors
}
export const validateClientDetails = (c: Case): ValidationError[] => validatePerson(c.clientDetails, { strict: true })
// ── Required accident fields BY CLAIM TYPE (B2b fix 2 + 3) ──────────
// THE rule — used by the Accident details card (stars + hints), Mark
// complete and the required-fields chip / count (validateAccidentDetails).
// (The server has no required-field checks for these — only types / dates.)
//   • accident date — optional for EL disease (often no single date: first
//     symptoms / diagnosis / knowledge instead); required otherwise, and
//     while no type is set yet;
//   • accident location — optional for EL disease (exposure is often at
//     several workplaces); required otherwise, and while no type is set;
//   • claimant registration — required for a road traffic (RTA) claim not on
//     the CICA scheme route, AND while no type is set yet (B2c: conservative —
//     most older cases are RTA); optional for every other type.
export type AccidentRequiredField = 'accidentDate' | 'accidentLocation' | 'claimantReg'
export const accidentFieldRequired = (field: AccidentRequiredField, c: Pick<Case, 'claim'>): boolean => {
  const t = c.claim?.claimType ?? null
  switch (field) {
    case 'accidentDate':
    case 'accidentLocation': return t !== 'EL_DISEASE'
    case 'claimantReg': return (t === null || t === 'RTA') && c.claim?.claimRoute !== 'CICA_SCHEME'
  }
  return true
}
export const accidentDateOptional = (c: Pick<Case, 'claim'>): boolean => !accidentFieldRequired('accidentDate', c)
export const DISEASE_LOCATION_HINT = 'Optional for disease claims: exposure is often at several workplaces.'
export const DISEASE_ACCIDENT_HINT = 'Disease claims often have no single accident date. Add first symptoms / diagnosis / date of knowledge in Case control → Claim instead.'
export const validateAccidentDetails = (c: Case): ValidationError[] => {
  const errors: ValidationError[] = []
  const d = c.accidentDetails
  if (!d.accidentDate && accidentFieldRequired('accidentDate', c)) errors.push({ field: 'accidentDate', message: 'Accident date is required' })
  // Mode-aware location check:
  // - freetext mode requires the textarea to be filled
  // - address mode requires at least a postcode in the structured fields
  //   (postcode is the most reliable lookup field; falls back to the
  //   legacy single-string `accidentLocation` for any pre-toggle data)
  if (!accidentFieldRequired('accidentLocation', c)) {
    // (B2b fix 3) optional for this claim type
  } else if (d.accidentLocationMode === 'freetext') {
    // Rich text: strip tags — an empty editor's "<p></p>" isn't content.
    if (!d.accidentLocationFreeText.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()) {
      errors.push({ field: 'accidentLocation', message: 'Accident location is required' })
    }
  } else {
    const hasStructured = d.accidentLocationAddressPostcode.trim().length > 0
    const hasLegacy = d.accidentLocation.trim().length > 0
    if (!hasStructured && !hasLegacy) {
      errors.push({ field: 'accidentLocation', message: 'Accident location is required' })
    }
  }
  if (!d.claimantReg.trim() && accidentFieldRequired('claimantReg', c)) errors.push({ field: 'claimantReg', message: 'Claimant registration is required' })
  return errors
}
// "At least one injury" — the injury areas (B3 adds hearing, lungs, skin,
// internal organs, fatal and other). Shared by the validator and the section
// flow (utils/sectionFlow).
export const INJURY_AREA_KEYS = [
  'neck', 'shoulder', 'back', 'ribsChestTorso', 'faceCheckboneJawNose', 'forearmWristHandFingers', 'damageToTeeth',
  'headSenses', 'hipsPelvisGenitals', 'legKneeAnkleFootToe', 'psychological',
  'hearingEars', 'lungsBreathing', 'skin', 'internalOrgans', 'fatal', 'other',
] as const
export const hasAnyInjury = (d: Case['injuryDetails']): boolean => INJURY_AREA_KEYS.some((k) => d[k] === true)
export const OTHER_INJURY_REQUIRED = 'Describe the other injury'
export const validateInjuryDetails = (c: Case): ValidationError[] => {
  const errors: ValidationError[] = []
  const d = c.injuryDetails
  if (!hasAnyInjury(d)) errors.push({ field: 'injuries', message: 'At least one injury must be selected' })
  // "Other (describe)" ticked → its description is required.
  if (d.other && !(d.otherDetails ?? '').trim()) errors.push({ field: 'injuryOtherDetails', message: OTHER_INJURY_REQUIRED })
  return errors
}
// Stage 2b: validateCase / isCaseValid / countErrors no longer take a
// Client arg — all per-section validation reads off Case (the snapshot
// is on c.clientDetails). The signatures simplified to (c: Case)
// throughout. Call sites (ClientDetailsSection, ValidationSummary,
// ViewingPaneHeader) updated to drop the client arg.
// B2: every case needs a claim type (Case control → Claim). Old cases have
// none, so the required-fields row nudges them. (A case without the claim
// group loaded — c.claim undefined — isn't flagged.)
export const validateClaim = (c: Case): ValidationError[] =>
  c.claim && !c.claim.claimType ? [{ field: 'claimType', message: 'Claim type is required' }] : []
export const validateCase = (c: Case): Record<string, ValidationError[]> => ({
  clientSource: validateClientSource(c.clientSource),
  clientDetails: validateClientDetails(c),
  accidentDetails: validateAccidentDetails(c),
  injuryDetails: validateInjuryDetails(c),
  automatic: validateClaim(c),
})
export const isCaseValid = (c: Case): boolean => {
  const errors = validateCase(c)
  return Object.values(errors).every((e) => e.length === 0)
}
export const countErrors = (c: Case): number => {
  const errors = validateCase(c)
  return Object.values(errors).reduce((total, e) => total + e.length, 0)
}
